// src/utils/guestSession.js
// 🟢 [访客模式] 单局成绩只存在本 tab 的 sessionStorage 里，永不写 Firestore。
//
// 设计要点：
// 1. 固定单 key —— 一个访客最多只有「一局」可领取的成绩（永远是最新那局），
//    不是数组也不是累加，从根本上限制了注册后补分的重放上限。
// 2. sessionStorage 而非 localStorage —— 只活在本 tab：F5 能保住成绩，关掉 tab 就没了。
//    用 localStorage 的话，成绩会跨天跨 tab 留存，可以被人挑一个高分反复领，
//    甚至会在同一浏览器后来登录的「另一个真实用户」头上加分。
// 3. 所有 storage 访问都包 try/catch —— Safari 隐私模式 / 内嵌 WebView 下
//    读写 sessionStorage 会直接抛异常，存不下也绝不能让游戏崩在结算画面。

const KEY = 'vocabrun.guestResult';

// 与 routes/games.js:16 的 MAX_GAME_POINTS 对齐。
// 服务端本来就会 clamp，这里只是让客户端不去「要」它拿不到的分数。
const MAX_CLAIMABLE = 500;

export const saveGuestResult = (result) => {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(result));
  } catch (e) {
    console.warn('[guest] result not persisted (storage blocked)');
  }
};

export const loadGuestResult = () => {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const r = JSON.parse(raw);
    // 结构校验：坏数据当作没有，避免把 NaN/undefined 提交给后端
    if (!r || typeof r.score !== 'number' || !Number.isFinite(r.score)) return null;
    if (!r.gameType) return null;
    return r;
  } catch (e) {
    return null;
  }
};

// 是否还有「值得领取」的成绩。0 分不算 —— 后端本来也不会给 0 分记分，
// 横幅上显示 "Claim your 0 points" 只会让人困惑。
export const hasClaimableGuestResult = () => {
  const r = loadGuestResult();
  return !!r && r.score > 0;
};

export const clearGuestResult = () => {
  try {
    sessionStorage.removeItem(KEY);
  } catch (e) {
    /* noop */
  }
};

// 转成 POST /api/games/score 的请求体。
// ⚠️ 字段名对齐 routes/games.js:21 实际 destructure 的那几个
// (wordsCorrect / wordsTotal / timeSpent)。现有三个游戏发的是
// wordsCompleted / duration，后端根本没读，会被静默丢掉。
export const toScorePayload = (r) => ({
  gameType: r.gameType,
  score: Math.min(Math.max(0, Math.floor(r.score) || 0), MAX_CLAIMABLE),
  accuracy: r.accuracy ?? 0,
  wordsCorrect: r.wordsCorrect ?? 0,
  wordsTotal: r.wordsTotal ?? 0,
  timeSpent: r.timeSpent ?? 0,
  level: 'core'
});
